import type { MoveId } from './exerciseMoves'

// Stick figures for each movement pattern, in a 100×100 box (ground at y=92),
// facing right. Poses are built from joint angles or reach targets (two-link IK)
// so limbs keep their lengths; the renderers (web SMIL, native Reanimated)
// interpolate joint positions between keyframes and loop.

type P = [number, number]
const TORSO = 26
const NECK = 4
const HEAD = 6.5
export const HEAD_R = 5.5
const UA = 13
const FA = 12
const TH = 18
const SH = 18
const FOOT = 5
const GROUND = 92
const ANKLE_Y = 89.5

const rad = (d: number) => (d * Math.PI) / 180
const dir = (d: number, l: number): P => [Math.cos(rad(d)) * l, Math.sin(rad(d)) * l]
const add = (a: P, b: P): P => [a[0] + b[0], a[1] + b[1]]
const r1 = (n: number) => Math.round(n * 10) / 10

// Elbow / knee for a limb from `a` reaching `to`; bend +1 / -1 picks the side.
function ik(a: P, to: P, l1: number, l2: number, bend: 1 | -1): [P, P] {
  const dx = to[0] - a[0]
  const dy = to[1] - a[1]
  const d = Math.min(Math.hypot(dx, dy), l1 + l2 - 0.01)
  const base = Math.atan2(dy, dx)
  const cos = (l1 * l1 + d * d - l2 * l2) / (2 * l1 * d)
  const off = Math.acos(Math.max(-1, Math.min(1, cos)))
  const j: P = [a[0] + Math.cos(base + bend * off) * l1, a[1] + Math.sin(base + bend * off) * l1]
  const end: P = [a[0] + Math.cos(base) * d, a[1] + Math.sin(base) * d]
  return [j, end]
}

type Limb = { ang: [number, number] } | { to: P; bend: 1 | -1 }

type Spec = {
  hip: P
  t: number
  head?: number
  arch?: number
  arms: [Limb, Limb]
  legs: [Limb, Limb]
  feet?: [number, number]
}

export type Frame = {
  spine: P[]
  head: P
  arms: [P[], P[]]
  legs: [P[], P[]]
}

function limb(from: P, l: Limb, l1: number, l2: number): [P, P] {
  if ('to' in l) return ik(from, l.to, l1, l2, l.bend)
  const j = add(from, dir(l.ang[0], l1))
  return [j, add(j, dir(l.ang[1], l2))]
}

function pose(s: Spec): Frame {
  const sh = add(s.hip, dir(s.t, TORSO))
  const neck = add(sh, dir(s.t, NECK))
  const head = add(neck, dir(s.head ?? s.t, HEAD))
  const mid = add([(s.hip[0] + sh[0]) / 2, (s.hip[1] + sh[1]) / 2], dir(s.t - 90, s.arch ?? 0))
  const arm = (l: Limb) => [sh, ...limb(sh, l, UA, FA)]
  const leg = (l: Limb, f: number) => {
    const [k, a] = limb(s.hip, l, TH, SH)
    return [s.hip, k, a, add(a, dir(f, FOOT))]
  }
  const feet = s.feet ?? [25, 25]
  const round = (ps: P[]) => ps.map(([x, y]) => [r1(x), r1(y)] as P)
  return {
    spine: round([s.hip, mid, sh, neck]),
    head: round([head])[0],
    arms: [round(arm(s.arms[0])), round(arm(s.arms[1]))],
    legs: [round(leg(s.legs[0], feet[0])), round(leg(s.legs[1], feet[1]))],
  }
}

// Holds breathe: the chest and everything above it rise a touch.
function breathe(f: Frame, dy = -1.3): Frame {
  const up = (p: P): P => (p[1] < GROUND - 3.5 ? [p[0], r1(p[1] + dy)] : p)
  return {
    spine: [f.spine[0], up(f.spine[1]), up(f.spine[2]), up(f.spine[3])],
    head: up(f.head),
    arms: [f.arms[0].map(up), f.arms[1].map(up)] as [P[], P[]],
    legs: f.legs,
  }
}

export type Prop =
  | { kind: 'line'; from: P; to: P }
  | { kind: 'circle'; at: P; r: number }
  | { kind: 'plate'; arm: 0 | 1 }

export type Figure = {
  frames: Frame[]
  // Seconds per loop.
  dur: number
  // Front view: both sides drawn at full strength.
  front?: boolean
  // Held pose (slow breathing loop).
  hold?: boolean
  // Frame shown under reduced motion.
  still: number
  props?: Prop[]
}

const stand = (x: number, arms: [Limb, Limb] = [{ ang: [95, 88] }, { ang: [85, 82] }]): Spec => ({
  hip: [x, ANKLE_Y - TH - SH + 0.02],
  t: -90,
  arms,
  legs: [
    { to: [x + 1, ANKLE_Y], bend: -1 },
    { to: [x - 1, ANKLE_Y], bend: -1 },
  ],
})

const rep = (frames: Spec[], props?: Prop[], dur = 1.8, still = 1): Figure => ({
  frames: frames.map(pose),
  dur,
  still,
  props,
})

const hold = (s: Spec, extra: Partial<Figure> = {}): Figure => {
  const f = pose(s)
  return { frames: [f, breathe(f)], dur: 4.5, hold: true, still: 0, ...extra }
}

const floorLine = (x1: number, x2: number): Prop => ({
  kind: 'line',
  from: [x1, GROUND],
  to: [x2, GROUND],
})

const pushUp = (down: boolean, ankle: P = [16, 88.5], hand: P = [74, 91]): Spec => {
  const body = 62
  const rise = (down ? 10 : 24) - (GROUND - ankle[1])
  const t = -Math.asin(rise / body) * (180 / Math.PI)
  const hip = add(ankle, dir(t, TH + SH))
  return {
    hip,
    t,
    head: t + 8,
    arms: [
      { to: hand, bend: 1 },
      { to: [hand[0] - 2, hand[1]], bend: 1 },
    ],
    legs: [
      { to: ankle, bend: 1 },
      { to: [ankle[0] - 1, ankle[1]], bend: 1 },
    ],
    feet: [100, 100],
  }
}

const hinge = (bent: boolean, arms?: [Limb, Limb]): Spec =>
  bent
    ? {
        hip: [36, 62],
        t: -25,
        head: -15,
        arms: arms ?? [{ ang: [90, 90] }, { ang: [88, 88] }],
        legs: [
          { to: [52, ANKLE_Y], bend: -1 },
          { to: [50, ANKLE_Y], bend: -1 },
        ],
      }
    : stand(48, [{ ang: [88, 85] }, { ang: [86, 84] }])

const lyingKnees = (t: number, hip: P, arms: [Limb, Limb]): Spec => ({
  hip,
  t,
  head: t - 5,
  arms,
  legs: [
    { to: [28, ANKLE_Y], bend: -1 },
    { to: [26, ANKLE_Y], bend: -1 },
  ],
  feet: [20, 20],
})

const bridgeUp = lyingKnees(28, [46, 73], [{ ang: [178, 180] }, { ang: [176, 180] }])

function runFrames(): Spec[] {
  const base = (
    hipY: number,
    near: [number, number],
    far: [number, number],
    aN: [number, number],
    aF: [number, number],
  ): Spec => ({
    hip: [50, hipY],
    t: -80,
    head: -82,
    arms: [{ ang: aN }, { ang: aF }],
    legs: [{ ang: near }, { ang: far }],
    feet: [15, 15],
  })
  const A = base(53, [45, 105], [125, 165], [125, 40], [55, -30])
  const M1 = base(51, [88, 95], [70, 150], [95, 15], [85, 15])
  const B = base(53, [125, 165], [45, 105], [55, -30], [125, 40])
  const M2 = base(51, [70, 150], [88, 95], [85, 15], [95, 15])
  return [A, M1, B, M2]
}

function cycleFrames(): Spec[] {
  const crank: P = [53, 76]
  return [0, 45, 90, 135, 180, 225, 270, 315].map((a) => ({
    hip: [42, 51],
    t: -38,
    head: -30,
    arms: [
      { to: [70, 49], bend: -1 },
      { to: [69, 49], bend: -1 },
    ],
    legs: [
      { to: add(crank, dir(a, 9)), bend: -1 },
      { to: add(crank, dir(a + 180, 9)), bend: -1 },
    ],
    feet: [20, 20],
  }))
}

const bike: Prop[] = [
  { kind: 'circle', at: [27, 79], r: 12 },
  { kind: 'circle', at: [77, 79], r: 12 },
  { kind: 'line', from: [27, 79], to: [53, 76] },
  { kind: 'line', from: [53, 76], to: [42, 54] },
  { kind: 'line', from: [42, 54], to: [27, 79] },
  { kind: 'line', from: [53, 76], to: [70, 52] },
  { kind: 'line', from: [70, 52], to: [77, 79] },
  { kind: 'line', from: [37, 53.5], to: [46, 53.5] },
  { kind: 'line', from: [70, 52], to: [72, 47] },
]

export const FIGURES: Record<Exclude<MoveId, 'generic'>, Figure> = {
  pushup: rep([pushUp(false), pushUp(true)], [floorLine(8, 92)]),
  squat: rep(
    [
      stand(48, [{ ang: [95, 88] }, { ang: [85, 82] }]),
      {
        hip: [37, 72],
        t: -58,
        head: -75,
        arms: [{ ang: [-5, -3] }, { ang: [-3, -1] }],
        legs: [
          { to: [50, ANKLE_Y], bend: -1 },
          { to: [48, ANKLE_Y], bend: -1 },
        ],
      },
    ],
    [floorLine(20, 80)],
  ),
  lunge: rep(
    [
      {
        hip: [50, 56],
        t: -90,
        arms: [{ ang: [95, 90] }, { ang: [86, 84] }],
        legs: [
          { to: [65, ANKLE_Y], bend: -1 },
          { to: [35, 87.5], bend: -1 },
        ],
        feet: [25, 60],
      },
      {
        hip: [50, 69],
        t: -90,
        arms: [{ ang: [95, 90] }, { ang: [86, 84] }],
        legs: [
          { to: [65, ANKLE_Y], bend: -1 },
          { to: [33, 87.5], bend: -1 },
        ],
        feet: [25, 60],
      },
    ],
    [floorLine(15, 85)],
  ),
  plank: hold(
    {
      hip: [51, 81],
      t: -10,
      head: -5,
      arms: [
        { to: [91, 90], bend: 1 },
        { to: [89, 90], bend: 1 },
      ],
      legs: [
        { to: [16, 88.5], bend: 1 },
        { to: [15, 88.5], bend: 1 },
      ],
      feet: [100, 100],
    },
    { props: [floorLine(8, 96)], dur: 3.5 },
  ),
  bench: rep(
    [
      {
        hip: [38, 66.5],
        t: 0,
        head: 0,
        arms: [{ ang: [-90, -90] }, { ang: [-92, -92] }],
        legs: [
          { to: [21, ANKLE_Y], bend: 1 },
          { to: [19, ANKLE_Y], bend: 1 },
        ],
      },
      {
        hip: [38, 66.5],
        t: 0,
        head: 0,
        arms: [
          { to: [62, 58], bend: 1 },
          { to: [61, 58], bend: 1 },
        ],
        legs: [
          { to: [21, ANKLE_Y], bend: 1 },
          { to: [19, ANKLE_Y], bend: 1 },
        ],
      },
    ],
    [
      { kind: 'line', from: [30, 71], to: [82, 71] },
      { kind: 'line', from: [36, 71], to: [36, GROUND] },
      { kind: 'line', from: [76, 71], to: [76, GROUND] },
      { kind: 'plate', arm: 0 },
    ],
  ),
  overhead: rep(
    [
      stand(48, [
        { to: [53, 22], bend: 1 },
        { to: [52, 22], bend: 1 },
      ]),
      stand(48, [{ ang: [-84, -86] }, { ang: [-86, -88] }]),
    ],
    [{ kind: 'plate', arm: 0 }, floorLine(25, 75)],
  ),
  hinge: rep([hinge(false), hinge(true)], [{ kind: 'plate', arm: 0 }, floorLine(25, 80)]),
  row: rep(
    [
      hinge(true),
      hinge(true, [
        { to: [52, 64], bend: -1 },
        { to: [51, 64], bend: -1 },
      ]),
    ],
    [{ kind: 'plate', arm: 0 }, floorLine(25, 80)],
  ),
  pullup: rep(
    [
      {
        hip: [52, 65],
        t: -90,
        arms: [
          { to: [57, 13], bend: 1 },
          { to: [55, 13], bend: 1 },
        ],
        legs: [{ ang: [97, 125] }, { ang: [95, 120] }],
        feet: [60, 60],
      },
      {
        hip: [51, 51],
        t: -90,
        head: -90,
        arms: [
          { to: [57, 13], bend: 1 },
          { to: [55, 13], bend: 1 },
        ],
        legs: [{ ang: [95, 125] }, { ang: [93, 120] }],
        feet: [60, 60],
      },
    ],
    [{ kind: 'line', from: [30, 12], to: [74, 12] }],
  ),
  curl: rep(
    [
      stand(48, [{ ang: [92, 88] }, { ang: [88, 86] }]),
      stand(48, [{ ang: [92, -62] }, { ang: [88, -64] }]),
    ],
    [{ kind: 'plate', arm: 0 }, floorLine(25, 75)],
  ),
  bridge: rep(
    [lyingKnees(-4, [45, 87.5], [{ ang: [178, 180] }, { ang: [176, 180] }]), bridgeUp],
    [floorLine(15, 90)],
  ),
  crunch: rep(
    [
      lyingKnees(-3, [44, 87.5], [{ ang: [178, 180] }, { ang: [176, 180] }]),
      lyingKnees(-38, [44, 87.5], [{ ang: [200, 192] }, { ang: [198, 190] }]),
    ],
    [floorLine(15, 90)],
  ),
  jumpingjack: {
    ...rep(
      [
        {
          hip: [50, 53.5],
          t: -90,
          arms: [{ ang: [80, 88] }, { ang: [100, 92] }],
          legs: [
            { to: [53, ANKLE_Y], bend: 1 },
            { to: [47, ANKLE_Y], bend: -1 },
          ],
          feet: [10, 170],
        },
        {
          hip: [50, 52],
          t: -90,
          arms: [{ ang: [-40, -65] }, { ang: [-140, -115] }],
          legs: [
            { to: [66, ANKLE_Y - 1], bend: 1 },
            { to: [34, ANKLE_Y - 1], bend: -1 },
          ],
          feet: [10, 170],
        },
      ],
      [floorLine(25, 75)],
      1.4,
    ),
    front: true,
  },
  burpee: rep(
    [
      stand(66),
      {
        hip: [60, 76],
        t: -40,
        head: -20,
        arms: [
          { to: [80, 91], bend: 1 },
          { to: [78, 91], bend: 1 },
        ],
        legs: [
          { to: [66, ANKLE_Y], bend: -1 },
          { to: [64, ANKLE_Y], bend: -1 },
        ],
      },
      pushUp(false, [22, 88.5], [80, 91]),
      {
        hip: [60, 76],
        t: -40,
        head: -20,
        arms: [
          { to: [80, 91], bend: 1 },
          { to: [78, 91], bend: 1 },
        ],
        legs: [
          { to: [66, ANKLE_Y], bend: -1 },
          { to: [64, ANKLE_Y], bend: -1 },
        ],
      },
      {
        ...stand(66, [{ ang: [-50, -62] }, { ang: [-58, -70] }]),
        hip: [66, 50],
        legs: [
          { to: [67, 85.5], bend: -1 },
          { to: [65, 85.5], bend: -1 },
        ],
        feet: [60, 60],
      },
    ],
    [floorLine(10, 90)],
    2.2,
    2,
  ),
  climber: rep(
    [
      {
        ...pushUp(false),
        legs: [
          { to: [52, 85], bend: 1 },
          { to: [16, 88.5], bend: 1 },
        ],
      },
      {
        ...pushUp(false),
        legs: [
          { to: [16, 88.5], bend: 1 },
          { to: [52, 85], bend: 1 },
        ],
      },
    ],
    [floorLine(8, 92)],
    1.2,
    0,
  ),
  run: rep(runFrames(), [floorLine(20, 80)], 1.2, 0),
  cycle: rep(cycleFrames(), bike, 1.4, 0),
  stretch: {
    ...rep(
      [
        stand(50, [{ ang: [-62, -80] }, { ang: [-118, -100] }]),
        { ...stand(50, [{ ang: [-48, -62] }, { ang: [-100, -80] }]), t: -76 },
        stand(50, [{ ang: [-62, -80] }, { ang: [-118, -100] }]),
        { ...stand(50, [{ ang: [-80, -100] }, { ang: [-132, -118] }]), t: -104 },
      ],
      [floorLine(25, 75)],
      4,
      1,
    ),
    front: true,
  },
  downdog: hold(
    {
      hip: [42, 52],
      t: 52,
      head: 125,
      arms: [{ ang: [52, 52] }, { ang: [54, 54] }],
      legs: [
        { to: [24, 88], bend: 1 },
        { to: [22, 88], bend: 1 },
      ],
      feet: [10, 10],
    },
    { props: [floorLine(10, 92)] },
  ),
  cobra: hold(
    {
      hip: [46, 85.5],
      t: -38,
      head: -62,
      arms: [
        { to: [68, 91], bend: 1 },
        { to: [66, 91], bend: 1 },
      ],
      legs: [
        { to: [11, 88.5], bend: 1 },
        { to: [10, 88.5], bend: 1 },
      ],
      feet: [175, 175],
    },
    { props: [floorLine(4, 92)] },
  ),
  child: hold(
    {
      hip: [34, 79],
      t: 10,
      head: 18,
      arms: [{ ang: [6, 3] }, { ang: [8, 4] }],
      legs: [
        { to: [30, 89], bend: -1 },
        { to: [28, 89], bend: -1 },
      ],
      feet: [180, 180],
    },
    { props: [floorLine(12, 96)] },
  ),
  catcow: {
    frames: [
      pose({
        hip: [35, 66],
        t: 0,
        arch: 6,
        head: 75,
        arms: [{ ang: [90, 90] }, { ang: [88, 90] }],
        legs: [
          { to: [17, 89], bend: -1 },
          { to: [15, 89], bend: -1 },
        ],
        feet: [180, 180],
      }),
      pose({
        hip: [35, 66],
        t: 0,
        arch: -5,
        head: -40,
        arms: [{ ang: [90, 90] }, { ang: [88, 90] }],
        legs: [
          { to: [17, 89], bend: -1 },
          { to: [15, 89], bend: -1 },
        ],
        feet: [180, 180],
      }),
    ],
    dur: 5,
    hold: true,
    still: 0,
    props: [floorLine(8, 80)],
  },
  warrior1: hold(
    {
      hip: [46, 64],
      t: -88,
      head: -88,
      arms: [{ ang: [-92, -94] }, { ang: [-88, -90] }],
      legs: [
        { to: [68, ANKLE_Y], bend: -1 },
        { to: [21, ANKLE_Y], bend: -1 },
      ],
      feet: [20, 150],
    },
    { props: [floorLine(10, 85)] },
  ),
  warrior2: hold(
    {
      hip: [50, 71],
      t: -90,
      arms: [{ ang: [0, 0] }, { ang: [180, 180] }],
      legs: [{ ang: [0, 90] }, { to: [23, ANKLE_Y], bend: -1 }],
      feet: [10, 170],
    },
    { front: true, props: [floorLine(12, 88)] },
  ),
  tree: hold(
    {
      hip: [50, 53.5],
      t: -90,
      arms: [
        { to: [51, 38], bend: -1 },
        { to: [49, 38], bend: 1 },
      ],
      legs: [
        { to: [50.5, 72], bend: -1 },
        { to: [50, ANKLE_Y], bend: 1 },
      ],
      feet: [180, 170],
    },
    { front: true, props: [floorLine(30, 70)] },
  ),
  triangle: hold(
    {
      hip: [48, 60],
      t: -18,
      head: -25,
      arms: [{ ang: [92, 90] }, { ang: [-88, -90] }],
      legs: [
        { to: [70, ANKLE_Y], bend: 1 },
        { to: [26, ANKLE_Y], bend: -1 },
      ],
      feet: [10, 170],
    },
    { front: true, props: [floorLine(12, 88)] },
  ),
  chair: hold(
    {
      hip: [40, 67],
      t: -62,
      head: -60,
      arms: [{ ang: [-74, -74] }, { ang: [-76, -76] }],
      legs: [
        { to: [53, ANKLE_Y], bend: -1 },
        { to: [51, ANKLE_Y], bend: -1 },
      ],
    },
    { props: [floorLine(25, 80)] },
  ),
  bridgepose: hold(bridgeUp, { props: [floorLine(15, 90)] }),
  forwardfold: hold(
    {
      hip: [28, 86.5],
      t: -22,
      head: -8,
      arms: [
        { to: [70, 85], bend: -1 },
        { to: [69, 85], bend: -1 },
      ],
      legs: [
        { to: [64, 88], bend: 1 },
        { to: [63, 88.5], bend: 1 },
      ],
      feet: [-80, -80],
    },
    { props: [floorLine(15, 85)] },
  ),
  lowlunge: hold(
    {
      hip: [46, 73],
      t: -84,
      head: -84,
      arms: [{ ang: [-88, -90] }, { ang: [-86, -88] }],
      legs: [
        { to: [70, ANKLE_Y], bend: -1 },
        { to: [20, 89], bend: -1 },
      ],
      feet: [20, 180],
    },
    { props: [floorLine(10, 85)] },
  ),
  mountain: hold(stand(50, [{ ang: [92, 90] }, { ang: [88, 90] }]), { props: [floorLine(30, 70)] }),
  pose: hold(
    {
      hip: [50, 80],
      t: -90,
      arms: [
        { to: [65, 82], bend: -1 },
        { to: [35, 82], bend: 1 },
      ],
      legs: [{ ang: [15, 165] }, { ang: [165, 15] }],
      feet: [180, 0],
    },
    { front: true, props: [floorLine(25, 75)] },
  ),
}

export const figureFor = (m: MoveId): Figure | null => (m === 'generic' ? null : FIGURES[m])

export const pts = (ps: P[]) => ps.map(([x, y]) => `${x},${y}`).join(' ')
